import { useState } from 'react'
import { Alert, AuthLayout, Button, Checkbox, Link, PasswordField, TextField } from 'armxlib'
import { PageMeta } from '../../app/PageMeta'
import { en } from '../../i18n/en'
import { isValidUsername, MIN_PASSWORD_LENGTH } from '../../lib/rules'
import { useCaptcha } from '../../lib/turnstile'
import { ROUTES } from '../../routes'
import { SiteFooter } from '../legal/SiteFooter'
import { confirmSignUp, failureReason, resendSignUpCode, signUp } from './api'
import { CodeStep } from './CodeStep'
import { useUsernameAvailability } from './hooks'

const t = en.auth

/**
 * `/register`: email, username, password, terms and Turnstile, then the code
 * received by email. The code signs the new user in.
 */
export function RegisterPage() {
  const [step, setStep] = useState<'form' | 'code'>('form')
  const [email, setEmail] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [mustAgree, setMustAgree] = useState(false)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const captcha = useCaptcha()
  const availability = useUsernameAvailability(username)

  const usernameError =
    username !== '' && !isValidUsername(username)
      ? t.usernameUnavailable.invalid
      : availability.data?.reason
        ? t.usernameUnavailable[availability.data.reason]
        : undefined

  async function submit() {
    setMustAgree(!agreed)
    if (!agreed || usernameError) return
    if (!captcha.token) {
      setError(captcha.failed ? t.captchaFailed : t.captchaPending)
      return
    }
    setPending(true)
    setError(null)
    try {
      await signUp({ email, password, username }, captcha.token)
      setStep('code')
    } catch (failure) {
      setError(t.errors[failureReason(failure)])
    } finally {
      captcha.reset()
      setPending(false)
    }
  }

  const meta = (
    <PageMeta title={t.register.title} description={t.register.metaDescription} canonicalPath={ROUTES.register.path} />
  )

  if (step === 'code') {
    return (
      <AuthLayout brand={en.site.name} title={t.code.title} description={t.code.description(email)} footer={<SiteFooter />}>
        {meta}
        <CodeStep
          submitLabel={t.register.confirm}
          onSubmit={(code) => confirmSignUp(email, code)}
          onResend={(captchaToken) => resendSignUpCode(email, captchaToken)}
        />
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      brand={en.site.name}
      title={t.register.title}
      footer={
        <>
          <span>
            {t.register.haveAccount} <Link href={ROUTES.login.path}>{t.register.signIn}</Link>
          </span>
          <SiteFooter />
        </>
      }
    >
      {meta}
      <form
        onSubmit={(event) => {
          event.preventDefault()
          void submit()
        }}
      >
        {error && <Alert tone="danger">{error}</Alert>}
        <TextField
          label={t.email}
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <TextField
          label={t.username}
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          required
          maxLength={20}
          value={username}
          onChange={(event) => setUsername(event.target.value.trim())}
          error={usernameError}
          hint={
            availability.data?.available && availability.data.reason === null && !usernameError ? (
              t.usernameAvailable
            ) : (
              <ul>
                {t.usernameRules.map((rule) => (
                  <li key={rule}>{rule}</li>
                ))}
              </ul>
            )
          }
        />
        <PasswordField
          label={t.password}
          autoComplete="new-password"
          required
          minLength={MIN_PASSWORD_LENGTH}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          hint={t.passwordHint(MIN_PASSWORD_LENGTH)}
        />
        <Checkbox
          checked={agreed}
          onChange={(event) => {
            setAgreed(event.target.checked)
            setMustAgree(false)
          }}
          error={mustAgree ? t.mustAgree : undefined}
          label={
            <>
              {t.agreePrefix} <Link href={ROUTES.terms.path}>{t.termsLink}</Link> {t.agreeMiddle}{' '}
              <Link href={ROUTES.privacy.path}>{t.privacyLink}</Link>
            </>
          }
        />
        {captcha.widget}
        <Button type="submit" variant="primary" fullWidth loading={pending}>
          {t.register.submit}
        </Button>
      </form>
    </AuthLayout>
  )
}
